export const ORGX_CLAUDE_PLUGIN_NAME = "orgx-claude-code-plugin";
export const ORGX_CLAUDE_MARKETPLACE_NAME = "orgx-local";
export const ORGX_CLAUDE_PLUGIN_VERSION = "0.1.12";
export const ORGX_CLAUDE_PLUGIN_REF = `v${ORGX_CLAUDE_PLUGIN_VERSION}`;
export const ORGX_CLAUDE_PLUGIN_MCP_URL =
  "https://mcp.useorgx.com/mcp?profile=claude-directory";

const ALLOWED_FILES = [
  ".claude-plugin/marketplace.json",
  ".claude-plugin/plugin.json",
  ".mcp.json",
  "commands/orgx-login.md",
  "commands/orgx-operator-chronicle.md",
  "commands/orgx-status.md",
  "skills/orgx-setup/SKILL.md",
] as const;

const PROHIBITED_CONTENT = [
  { label: "transcript access", pattern: /\btranscripts?\b/i },
  {
    label: "telemetry collection",
    pattern: /\b(?:collect|capture|record|send|upload|emit|track|enable|configure)\b[^\n]{0,80}\btelemetry\b/i,
  },
  {
    label: "telemetry collection",
    pattern: /\btelemetry\b[^\n]{0,40}\b(?:collection|capture|recording|upload|emission|tracking|enabled)\b/i,
  },
  {
    label: "telemetry collection",
    pattern: /\btelemetry\b\s*[:=]\s*true\b/i,
  },
  { label: "dynamic context sync", pattern: /dynamic(?:[\s_-]+context)?[\s_-]+sync/i },
  { label: "runtime hook", pattern: /orgx-session-hook|emit-execution-graph|post-install\.sh/i },
] as const;

const MANIFEST_CAPABILITY_FIELDS = [
  "agents",
  "commands",
  "hooks",
  "lspServers",
  "mcpServers",
  "skills",
] as const;

const EXPECTED_PLUGIN_MANIFEST = {
  $schema: "https://json.schemastore.org/claude-code-plugin-manifest.json",
  name: ORGX_CLAUDE_PLUGIN_NAME,
  displayName: "OrgX",
  version: ORGX_CLAUDE_PLUGIN_VERSION,
  description: "Connect Claude Code to a focused, non-destructive, closed-world OrgX status profile through native OAuth.",
  author: {
    name: "OrgX Team",
    email: "reviewers@useorgx.com",
    url: "https://useorgx.com",
  },
  homepage: "https://useorgx.com",
  repository: "https://github.com/useorgx/orgx-claude-code-plugin",
  license: "MIT",
  keywords: [
    "orgx",
    "mcp",
    "productivity",
    "status",
    "native-oauth",
  ],
} as const;

function parseJsonFile(
  files: ReadonlyMap<string, Buffer>,
  path: string,
): Record<string, unknown> {
  const bytes = files.get(path);
  if (!bytes) {
    throw new Error(`Rejected Claude plugin bundle: required file '${path}' is missing.`);
  }

  let value: unknown;
  try {
    value = JSON.parse(bytes.toString("utf8"));
  } catch {
    throw new Error(`Rejected Claude plugin bundle: '${path}' is not valid JSON.`);
  }

  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`Rejected Claude plugin bundle: '${path}' must contain a JSON object.`);
  }

  return value as Record<string, unknown>;
}

function assertExactKeys(
  value: Record<string, unknown>,
  expected: readonly string[],
  label: string,
): void {
  const actualKeys = Object.keys(value).sort();
  const expectedKeys = [...expected].sort();
  if (actualKeys.length !== expectedKeys.length ||
    actualKeys.some((key, index) => key !== expectedKeys[index])) {
    throw new Error(
      `Rejected Claude plugin bundle: ${label} must contain only ${expectedKeys.join(", ")}.`,
    );
  }
}

function canonicalizeJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((item) => canonicalizeJson(item)).join(",")}]`;
  }
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right));
    return `{${entries.map(([key, item]) => `${JSON.stringify(key)}:${canonicalizeJson(item)}`).join(",")}}`;
  }
  return JSON.stringify(value) ?? "undefined";
}

export function validateClaudePluginBundle(files: ReadonlyMap<string, Buffer>): void {
  const actualFiles = [...files.keys()].sort();
  const expectedFiles = [...ALLOWED_FILES].sort();
  const missingFiles = expectedFiles.filter((path) => !files.has(path));
  const unexpectedFiles = actualFiles.filter(
    (path) => !ALLOWED_FILES.includes(path as typeof ALLOWED_FILES[number]),
  );
  if (missingFiles.length > 0 || unexpectedFiles.length > 0) {
    const details = [
      ...(missingFiles.length > 0 ? [`missing ${missingFiles.join(", ")}`] : []),
      ...(unexpectedFiles.length > 0 ? [`unexpected ${unexpectedFiles.join(", ")}`] : []),
    ].join("; ");
    throw new Error(`Rejected Claude plugin bundle: file set is not allowed (${details}).`);
  }

  const manifest = parseJsonFile(files, ".claude-plugin/plugin.json");
  if (manifest.name !== ORGX_CLAUDE_PLUGIN_NAME || manifest.version !== ORGX_CLAUDE_PLUGIN_VERSION) {
    throw new Error(
      `Rejected Claude plugin bundle: manifest must identify ${ORGX_CLAUDE_PLUGIN_NAME}@${ORGX_CLAUDE_PLUGIN_VERSION}.`,
    );
  }
  const capabilityFields = MANIFEST_CAPABILITY_FIELDS.filter((field) => field in manifest);
  if (capabilityFields.length > 0) {
    throw new Error(
      `Rejected Claude plugin bundle: manifest cannot declare capability fields (${capabilityFields.join(", ")}).`,
    );
  }
  if (canonicalizeJson(manifest) !== canonicalizeJson(EXPECTED_PLUGIN_MANIFEST)) {
    throw new Error(
      "Rejected Claude plugin bundle: manifest does not match the reviewed v0.1.12 metadata contract.",
    );
  }

  const marketplace = parseJsonFile(files, ".claude-plugin/marketplace.json");
  if (marketplace.version !== ORGX_CLAUDE_PLUGIN_VERSION) {
    throw new Error(
      `Rejected Claude plugin bundle: marketplace version must be ${ORGX_CLAUDE_PLUGIN_VERSION}.`,
    );
  }

  const mcp = parseJsonFile(files, ".mcp.json");
  assertExactKeys(mcp, ["mcpServers"], ".mcp.json");
  const mcpServers = mcp.mcpServers;
  if (!mcpServers || typeof mcpServers !== "object" || Array.isArray(mcpServers)) {
    throw new Error("Rejected Claude plugin bundle: mcpServers must be a JSON object.");
  }
  const servers = mcpServers as Record<string, unknown>;
  assertExactKeys(servers, ["orgx"], "mcpServers");
  const orgx = servers.orgx;
  if (!orgx || typeof orgx !== "object" || Array.isArray(orgx)) {
    throw new Error("Rejected Claude plugin bundle: the orgx MCP server must be a JSON object.");
  }
  const orgxServer = orgx as Record<string, unknown>;
  assertExactKeys(orgxServer, ["type", "url"], "the orgx MCP server");
  if (orgxServer.type !== "http" || orgxServer.url !== ORGX_CLAUDE_PLUGIN_MCP_URL) {
    throw new Error(
      "Rejected Claude plugin bundle: the orgx MCP server must match the reviewed closed-world status profile endpoint.",
    );
  }

  const combinedContent = actualFiles
    .map((path) => files.get(path)?.toString("utf8") ?? "")
    .join("\n");
  for (const prohibited of PROHIBITED_CONTENT) {
    if (prohibited.pattern.test(combinedContent)) {
      throw new Error(
        `Rejected Claude plugin bundle: prohibited ${prohibited.label} content was found.`,
      );
    }
  }
}
